import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import { ProductFormUtils } from '../../utils/productCommon';
import { getBrandListFlat, type FlatBrandNode } from '../../utils/brand';
import {
  createProductOptionGroup,
  type ProductOptionGroupApiErrorItem,
} from '../../utils/productOptionGroup';
import ProductOptionGroupFormLayout, {
  type ProductOptionGroupTabId,
} from './components/ProductOptionGroupFormLayout';
import ProductOptionGroupInfoTab from './components/ProductOptionGroupInfoTab';
import {
  DEFAULT_OPTION_GROUP_FORM,
  type ProductOptionGroupFormData,
  type ProductOptionGroupFormErrors,
} from './components/productOptionGroupFormTypes';
import { AlertCircle } from 'lucide-react';

const ProductOptionGroupCreate: React.FC = () => {
  const navigate = useNavigate();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-option-group-list');

  const [activeTab, setActiveTab] = useState<ProductOptionGroupTabId>('info');
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);

  const [formData, setFormData] = useState<ProductOptionGroupFormData>(DEFAULT_OPTION_GROUP_FORM);
  const [errors, setErrors] = useState<ProductOptionGroupFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  ProductFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const p = ProductFormUtils.loadProjectDetail();
    if (p) setProjectDetail(p);
  }, []);

  useEffect(() => {
    if (!projectDetail) return;
    const pc = projectDetail.code || getProjectCode();
    if (!pc) return;
    let cancelled = false;
    getBrandListFlat(pc)
      .then((res) => {
        if (!cancelled && res?.brands) setBrands(res.brands);
      })
      .catch((e) => {
        if (!cancelled) console.error(e);
      });
    return () => {
      cancelled = true;
    };
  }, [projectDetail?.code]);

  const validate = (): boolean => {
    const e: ProductOptionGroupFormErrors = {};
    if (!formData.brandCode?.trim()) e.brandCode = '브랜드를 선택해주세요.';
    if (!formData.code?.trim()) e.code = '그룹 코드를 입력해주세요.';
    if (!formData.name?.trim()) e.name = '그룹 명칭을 입력해주세요.';
    if (formData.minSelectOptions > formData.maxSelectOptions) {
      e.minSelectOptions = '최소 선택 옵션 수가 최대를 초과할 수 없습니다.';
    }
    if (formData.minOptionQtySum > formData.maxOptionQtySum && formData.maxOptionQtySum > 0) {
      e.minOptionQtySum = '최소 수량 합이 최대를 초과할 수 없습니다.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const mapApiErrorsToForm = (items: ProductOptionGroupApiErrorItem[]): ProductOptionGroupFormErrors => {
    const mapped: ProductOptionGroupFormErrors = {};
    const fieldMap: Record<string, keyof ProductOptionGroupFormErrors> = {
      brandCode: 'brandCode',
      code: 'code',
      status: 'status',
      name: 'name',
      mandatory: 'status',
      multi: 'status',
      maxSelect: 'maxSelectOptions',
      minSelect: 'minSelectOptions',
      maxSumQuantity: 'maxOptionQtySum',
      minSumQuantity: 'minOptionQtySum',
    };
    items.forEach((e) => {
      const key = fieldMap[e.target];
      if (key && e.message) mapped[key] = e.message;
    });
    return mapped;
  };

  const handleInfoSubmit = async () => {
    setSubmitError(null);
    if (!validate()) return;
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode) {
      setSubmitError('프로젝트 코드를 찾을 수 없습니다.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createProductOptionGroup(projectCode, {
        brandCode: formData.brandCode,
        code: formData.code.trim(),
        status: formData.status,
        name: formData.name,
        mandatory: formData.required ? 1 : 0,
        multi: formData.multiSelect ? 1 : 0,
        maxSelect: formData.maxSelectOptions,
        minSelect: formData.minSelectOptions,
        maxSumQuantity: formData.maxOptionQtySum,
        minSumQuantity: formData.minOptionQtySum,
      });
      if (!result.success) {
        if (result.errors?.length) {
          setErrors((prev) => ({ ...prev, ...mapApiErrorsToForm(result.errors || []) }));
        }
        setSubmitError(result.message || '옵션 그룹 생성에 실패했습니다.');
        return;
      }

      const savedCode = result.data?.code || formData.code.trim();
      navigate(
        `/products/option-groups/${encodeURIComponent(savedCode)}/update?tab=products`,
        { state: { formData: { ...formData } } }
      );
    } catch (err) {
      setSubmitError(CommonUtils.getNetworkErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(DEFAULT_OPTION_GROUP_FORM);
    setErrors({});
    setSubmitError(null);
  };

  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="옵션그룹 생성"
          subtitle={projectDetail.name}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo
          projectDetail={projectDetail}
        />
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}
            <ProductOptionGroupFormLayout
              activeTab={activeTab}
              onTabChange={setActiveTab}
              groupCode={null}
              onBackClick={() => navigate('/products/option-groups')}
            >
              {activeTab === 'info' && (
                <ProductOptionGroupInfoTab
                  mode="create"
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  brands={brands}
                  onSubmit={handleInfoSubmit}
                  onReset={handleReset}
                  onCancel={() => navigate('/products/option-groups')}
                  isSubmitting={isSubmitting}
                  submitButtonText="저장 후 상품 선택"
                />
              )}
            </ProductOptionGroupFormLayout>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductOptionGroupCreate;